import { useEffect, useRef } from 'react'

// Default debounce delay for popovers
const DEFAULT_DEBOUNCE_DELAY_MS = 350

/**
 * useDebouncedEffect
 *
 * A hook that debounces the execution of a callback function.
 * The callback is executed after the specified delay, and any pending
 * executions are cancelled when dependencies change.
 *
 * Useful for debouncing UI changes like showing/hiding popovers, tooltips,
 * or other visual feedback that shouldn't appear/disappear too quickly.
 *
 * @param callback - The function to execute after the debounce delay
 * @param deps - The dependencies that trigger the debounce
 * @param delay - The delay in milliseconds (defaults to 350ms)
 */
export function useDebouncedEffect(callback: () => void, deps: React.DependencyList, delay: number = DEFAULT_DEBOUNCE_DELAY_MS) {
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    // Schedule the callback
    debounceRef.current = setTimeout(() => {
      callback()
      debounceRef.current = null
    }, delay)

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
    // We intentionally use the deps parameter passed by the caller, not auto-detected deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
